import React, { useRef, useState } from 'react'
import { useGetTimeInMaterialReport } from './hooks'
import { useParams, useNavigate } from 'react-router-dom'
import { jsPDF } from 'jspdf'
import {
  Box,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Fetching } from '../../../components/Fetching'
import { ArrowBack } from '@mui/icons-material'
import { formatDateMMDDYYYY, formattedDate, formatTime } from '../../../lib/time'
import html2canvas from 'html2canvas'
import { TimeInMaterialReportExportNew } from './TimeInMaterialExportNew'
import { BackButton } from '../../../components/navigation/BackButton'

export const TimeInMaterialReportExport = () => {
  const { id, projectId, tnmId } = useParams()
  const { data, isFetching } = useGetTimeInMaterialReport(
    'timematerialreport',
    id
  )
  const pdfRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate();
  const tnmReportRef = useRef({}) as React.MutableRefObject<any>
  const [isDownloadPdf, setIsDownloadPdf] = useState(false);

  const mapTableData = (timesheets: any) => {
    return timesheets.map((timesheet: any) => [
      `${timesheet.user.firstName} ${timesheet.user.lastName}`,
      formatDateMMDDYYYY(timesheet.formattedTimeDurations.startTime),
      timesheet.formattedTimeDurations.totalTimeWorked,
      timesheet.formattedTimeDurations.regularTimeWorked,
      timesheet.formattedTimeDurations.overTimeWorked,
      timesheet.formattedTimeDurations.doubleOverTimeWorked,
      timesheet.costCodes
        .map((code: any) => `${code.percentage}% - ${code.costCode.label}`)
        .join(', '),
    ])
  }

  const handleExport = async () => {
    await setIsDownloadPdf(true);
  
    const input: any = tnmReportRef.current;
    html2canvas(input).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
      });
  
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      let widthToFit, heightToFit;
  
      if (imgProps.width > pdfWidth) {
        widthToFit = pdfWidth;
        heightToFit = (imgProps.height * pdfWidth) / imgProps.width;
      } else {
        widthToFit = imgProps.width;
        heightToFit = imgProps.height;
      }
  
      let currentPage = 1;
      const totalPages = Math.ceil(heightToFit / pdfHeight);
  
      const addFooter = () => {
        pdf.setFontSize(6);
        pdf.setTextColor(151, 154, 171);
        pdf.text(`Page ${currentPage} of ${totalPages}`, pdf.internal.pageSize.getWidth() - 16, pdf.internal.pageSize.getHeight() - 3);
        pdf.text(` Created by ${data?.project?.clientName} with Autodesk® Construction CloudTM on ${formattedDate(data?.project?.projectDueDate)} at ${formatTime(data?.project?.projectDueDate)}`, 5, pdf.internal.pageSize.getHeight() - 3);
      };
  
      const addHeader = () => {
        pdf.setFontSize(6);
        pdf.setTextColor(151, 154, 171);
        pdf.text(`Form detail report`, 185, 5);
        pdf.text(`New Multi Use Facility, Parking Lot & Stadium Entrance`, 5, 5);
      };
  
      for (let i = 0; i < totalPages; i++) {
        if (i > 0) {
          pdf.addPage();
          currentPage++;
        }
        pdf.addImage(imgData, 'PNG', 0, -pdfHeight * i, widthToFit, heightToFit);
        addHeader();
        addFooter();
      }
  
        pdf.save(`time-n-material-report-${data.project.projectName}-${data.displayReportNumber}.pdf`);

        setIsDownloadPdf(false);
      });

  }

  if (isFetching) {
    return <Fetching />
  }

  const getHours = (
    workStartDateTimeUTC: any,
    workEndDateTimeUTC: any
  ): any => {
    if (workStartDateTimeUTC && workEndDateTimeUTC) {
      const date1: any = new Date(workStartDateTimeUTC)
      const date2: any = new Date(workEndDateTimeUTC)
      const differenceInMilliseconds = date2 - date1
      const hours = differenceInMilliseconds / (60 * 60 * 1000)
      return hours.toFixed(2)
    } else return ''
  }

  return (
    <>
      {data ? (
        <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', mb: 2 }}>
            <BackButton steps={-2} />
            <Button
              sx={{ marginLeft: 2, marginBottom: 5, cursor: 'pointer' }}
              variant='contained'
              onClick={handleExport}>
              Download PDF
            </Button>
          </Box>
          <Box
            ref={pdfRef}
            sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
            <Typography variant='h4' gutterBottom>
              Time In Materials Report
            </Typography>
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Box sx={{ mt: 2, mb: 2 }}>
                <Box>
                  <strong>Project No.:</strong> {data.project.projectNumber}
                </Box>
                <Box>
                  <strong>Report Name:</strong> {data.name}
                </Box>
                <Box>
                  <strong>Report No.:</strong> {data.displayReportNumber}
                </Box>
                <Box>
                  <strong>Change Event:</strong> {data.changeEvent}
                </Box>
                <Box>
                  <strong>Date:</strong> {data.date}
                </Box>
              </Box>
              {/* <Box>
                <strong>Date:</strong>{' '}
                {new Intl.DateTimeFormat('en-US').format(new Date(data.date))}
              </Box> */}
            </Box>
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Typography sx={{ mt: 2 }} variant='h6' gutterBottom>
                General Information
              </Typography>
              <Box sx={{ mt: 2, mb: 3 }}>
                <Box>
                  <strong>Project:</strong> {data.project.projectName}
                </Box>
                <Box>
                  <strong>Location:</strong> {data.project.locations[0].address}
                </Box>
                <Box>
                  <strong>Weather:</strong> {data.weather}
                </Box>
                <Box>
                  <strong>Client:</strong> {data.project.clientName}
                </Box>
                <Box>
                  <strong>Service:</strong> {data.project.service}
                </Box>
              </Box>
            </Box>

            {/* ... other sections ... */}
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Typography sx={{ mt: 2 }} variant='h6' gutterBottom>
                Timesheets
              </Typography>
              <Box sx={{ mt: 2, mb: 3 }}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Hours</TableCell>
                        <TableCell>Cost Code</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.timecards ? (
                        data.timecards.map((timesheet: any, index: number) => {
                          return (
                            <TableRow key={`timesheet${index}`}>
                              <TableCell>
                                {timesheet.user.firstName}{' '}
                                {timesheet.user.lastName}
                              </TableCell>
                              {/* <TableCell>
                                {timesheet.totalTimeWorkedInHours
                                  ? timesheet.totalTimeWorkedInHours.toFixed(2)
                                  : ''}
                              </TableCell> */}
                              <TableCell>
                                {
                                  timesheet.formattedTimeDurations
                                    .totalTimeWorked
                                }
                              </TableCell>
                              <TableCell>
                                {timesheet.costCodes
                                  ? timesheet.costCodes.map(
                                    (costCode: any) =>
                                      `${costCode.percentage}% - ${costCode.costCode.label} `
                                  )
                                  : null}
                              </TableCell>
                            </TableRow>
                          )
                        })
                      ) : (
                        <TableRow key={'timesheet0'}>
                          <TableCell>
                            No timesheets were found for the report date.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Typography sx={{ mt: 2 }} variant='h6' gutterBottom>
                Equipment
              </Typography>
              <Box sx={{ mt: 2, mb: 3 }}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        {/* <TableCell>Quantity</TableCell>
                    <TableCell>#</TableCell>
                    <TableCell>Equipment Supplier</TableCell> */}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.equipment && data.equipment.length ? (
                        data.equipment.map((item: any, index: number) => {
                          return (
                            <TableRow key={`equipment${index}`}>
                              <TableCell>{item.lineItemValue}</TableCell>
                            </TableRow>
                          )
                        })
                      ) : (
                        <TableRow key={'timesheet0'}>
                          <TableCell>
                            No equipment was input for the report day.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Typography sx={{ mt: 2 }} variant='h6' gutterBottom>
                Material Delivered to Site Today
              </Typography>
              <Box sx={{ mt: 2, mb: 3 }}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        {/* <TableCell>Size</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Notes</TableCell> */}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.materialsDelivered &&
                        data.materialsDelivered.length ? (
                        data.materialsDelivered.map(
                          (item: any, index: number) => {
                            return (
                              <TableRow key={`material${index}`}>
                                <TableCell>{item.lineItemValue}</TableCell>
                              </TableRow>
                            )
                          }
                        )
                      ) : (
                        <TableRow key={'material0'}>
                          <TableCell>
                            No materials were delivered for the report date.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Typography sx={{ mt: 2 }} variant='h6' gutterBottom>
                Work Performed Today
              </Typography>
              <Box sx={{ mt: 2, mb: 3 }}>
                <Box style={{ whiteSpace: 'pre-wrap' }}>
                  {data.descriptionOfWork}
                </Box>
              </Box>
            </Box>
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Typography sx={{ mt: 2 }} variant='h6' gutterBottom>
                Causing Delays in Progress
              </Typography>
              <Box sx={{ mt: 2, mb: 3 }}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{data.causingDelaysInProgress ? data.causingDelaysInProgress : "No delays/impacts."}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Box sx={{ mt: 2, mb: 2 }}>
                <Box>
                  <strong>Authorized By:</strong> {data.authorizedBy}
                </Box>
              </Box>
            </Box>
          </Box>
          <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
            <Box sx={{ mt: 2, mb: 2 }}>
              <Box>
                <strong>Employee Signature: </strong>{data.employeeSignature}
              </Box>
              <Box sx={{ mt: 2, mb: 2 }}>
                <strong>Client Signature: </strong>{data.clientSignature}
              </Box>
            </Box>
            <Box sx={{ mt: 2, mb: 2 }}>
              <Box>
                <strong>IOR Signature: </strong>{data.iorSignature}
              </Box>
            </Box>
          </Box>
        </Box>
      ) : null}
      <Box ref={tnmReportRef} sx={{ display: isDownloadPdf ? 'block' : 'none', width:950,}} >
        <TimeInMaterialReportExportNew />
      </Box>
    </>
  )
}
